// KMS Client-Side Encryption Service
// Added by Vinaya - PII Encryption (AWS KMS integration) for mobile number fields

import { fetchApi } from './api';

class KMSClientEncryption {
  constructor() {
    this.dataKey = null;
    this.encryptedDataKey = null;
    this.decryptionCache = new Map(); // Cache decrypted values
  }

  // Fetch data encryption key from backend KMS endpoint
  initializeDataKey = async () => {
    const response = await fetchApi('/api/Encryption/GetEncryptionKey', { method: 'GET', skipLoader: true });
    if (response?.plaintextKey && response?.encryptedKey) {
      this.dataKey = response.plaintextKey;
      this.encryptedDataKey = response.encryptedKey;
      return true;
    }
    return false;
  };

  isReady() {
    return this.dataKey !== null && this.encryptedDataKey !== null;
  }

  // Check if a value appears to be encrypted (base64 format with sufficient length)
  isEncrypted(value) {
    if (!value || typeof value !== 'string') return false;
    
    // Base64 pattern with padding and must be longer than typical usernames
    const base64Pattern = /^[A-Za-z0-9+/]*={0,2}$/;
    return value.length > 20 && base64Pattern.test(value) && !value.includes('@');
  }

  // Encrypt a single field value using AES-256-GCM with KMS data key
  encryptField = async (plaintext) => {
    if (!plaintext) return plaintext;
    if (!this.isReady()) {
      await this.initializeDataKey();
    }

    const keyData = Uint8Array.from(atob(this.dataKey), c => c.charCodeAt(0));
    const aesKey = await window.crypto.subtle.importKey(
      'raw', keyData, { name: 'AES-GCM' }, false, ['encrypt']
    );
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encoder = new TextEncoder();
    const encrypted = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv }, aesKey, encoder.encode(plaintext)
    );

    // Combine IV + ciphertext (GCM tag is appended by Web Crypto API)
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    return btoa(String.fromCharCode(...combined));
  };

  // Decrypt a single field value using AES-256-GCM with KMS data key
  decryptField = async (encryptedText) => {
    if (!encryptedText || !this.isEncrypted(encryptedText)) {
      return encryptedText; // Return as-is if not encrypted
    }

    // Check cache first
    if (this.decryptionCache.has(encryptedText)) {
      return this.decryptionCache.get(encryptedText);
    }

    try {
      if (!this.isReady()) {
        await this.initializeDataKey();
      }

      const keyData = Uint8Array.from(atob(this.dataKey), c => c.charCodeAt(0));
      const aesKey = await window.crypto.subtle.importKey(
        'raw', keyData, { name: 'AES-GCM' }, false, ['decrypt']
      );

      // Decode the base64 encrypted text
      const encryptedBytes = Uint8Array.from(atob(encryptedText), c => c.charCodeAt(0));
      
      // Extract IV (first 12 bytes) and ciphertext (remaining bytes)
      const iv = encryptedBytes.slice(0, 12);
      const ciphertext = encryptedBytes.slice(12);

      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv }, aesKey, ciphertext
      );

      const decoder = new TextDecoder();
      const decryptedText = decoder.decode(decrypted);
      
      // Cache the result
      this.decryptionCache.set(encryptedText, decryptedText);
      
      return decryptedText;
    } catch (error) {
      console.warn('[KMS] Decryption failed for value:', encryptedText.substring(0, 20) + '...', error);
      return encryptedText; // Return original if decryption fails
    }
  };

  // Encrypt email field - alias for encryptField for consistency
  encryptEmail = async (plainEmail) => {
    return await this.encryptField(plainEmail);
  };

  // Returns the encrypted data key to send along with the payload
  getEncryptedDataKey() {
    return this.encryptedDataKey;
  }

  // Clear decryption cache (useful for memory management)
  clearCache() {
    this.decryptionCache.clear();
  }
}

const kmsEncryption = new KMSClientEncryption();
export default kmsEncryption;
